import { forwardRef, useId, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import { cn } from '../cn';

const control =
  'block w-full rounded-xl border border-line bg-white/90 px-3 text-sm text-ink placeholder:text-muted transition ' +
  'focus:border-brand-500 focus:outline-none focus:ring-4 focus:ring-violet/10 disabled:bg-canvas disabled:text-muted ' +
  'aria-[invalid=true]:border-danger aria-[invalid=true]:ring-red-100';

interface FieldProps {
  label: ReactNode;
  error?: string | undefined;
  hint?: ReactNode;
  id?: string;
  className?: string;
  children: (props: { id: string; 'aria-invalid'?: boolean; 'aria-describedby'?: string }) => ReactNode;
}

/** Rótulo, mensagem de erro e dica ligados ao controle por id e aria-describedby. */
export function Field({ label, error, hint, id, className, children }: FieldProps) {
  const auto = useId();
  const controlId = id ?? auto;
  const describedBy = error ? `${controlId}-error` : hint ? `${controlId}-hint` : undefined;
  return (
    <div className={cn('space-y-1', className)}>
      <label htmlFor={controlId} className="block text-sm font-medium text-ink">
        {label}
      </label>
      {children({ id: controlId, ...(error ? { 'aria-invalid': true } : {}), ...(describedBy ? { 'aria-describedby': describedBy } : {}) })}
      {error ? (
        <p id={`${controlId}-error`} className="text-sm text-danger">
          {error}
        </p>
      ) : hint ? (
        <p id={`${controlId}-hint`} className="text-xs text-muted">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(function Input({ className, ...rest }, ref) {
  return <input ref={ref} className={cn(control, 'h-10', className)} {...rest} />;
});

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(function Textarea({ className, ...rest }, ref) {
  return <textarea ref={ref} className={cn(control, 'min-h-24 py-2', className)} {...rest} />;
});

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(function Select({ className, children, ...rest }, ref) {
  return (
    <select ref={ref} className={cn(control, 'h-10 pr-8', className)} {...rest}>
      {children}
    </select>
  );
});

interface TextFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: ReactNode;
  error?: string | undefined;
  hint?: ReactNode;
}

/** Atalho para o caso comum: rótulo + input + erro. */
export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField({ label, error, hint, id, className, ...rest }, ref) {
  return (
    <Field label={label} error={error} hint={hint} id={id} className={className}>
      {(a11y) => <Input ref={ref} {...a11y} {...rest} />}
    </Field>
  );
});

export function Checkbox({ label, className, ...rest }: InputHTMLAttributes<HTMLInputElement> & { label: ReactNode }) {
  return (
    <label className={cn('inline-flex items-center gap-2 text-sm text-ink', className)}>
      <input type="checkbox" className="size-4 rounded border-line accent-brand-600 focus:ring-brand-500" {...rest} />
      {label}
    </label>
  );
}
